import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import {
  useOperatorsPricingRulesMutation,
  useOperatorsVehicleClassesQuery,
} from 'api/queries/operators';
import { useRef } from 'react';
import { UseFormReturn, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledField, SliderInputField, VehicleClass, colors } from 'remote/global';
import { useOperatorCurrency, useOperatorDistanceUnit } from '@/api/queries/users';
import { EndAdornment, TooltipTableHeader } from '../form-helpers';
import { CarrierPricingCostsSchemaType, carrierPricingCostsSchema } from './types';

export function CarrierVehicleClassPricingFields({
  form,
}: {
  form: UseFormReturn<any>; // TODO: Fix typing
}) {
  const { data: vehicleClasses } = useOperatorsVehicleClassesQuery();
  const tInstance = useTranslation();
  const { t } = tInstance;
  const { control } = form;
  const { isBasicAvailable, isStandardAvailable, isExecutiveAvailable } = {
    isBasicAvailable: vehicleClasses?.includes(VehicleClass.BASIC),
    isStandardAvailable: vehicleClasses?.includes(VehicleClass.STANDARD),
    isExecutiveAvailable: vehicleClasses?.includes(VehicleClass.EXECUTIVE),
  };

  const sizeRef = useRef<HTMLDivElement | null>(null);

  return (
    <Grid container gap={3}>
      <Grid item gap={1}>
        <Typography variant='subtitle2' mb={1}>
          {t(`PricingView.cost.vehicleClass.rates.${VehicleClass.EXECUTIVE}`)}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} sx={{ width: '100%' }}>
            <Box component={Paper} elevation={3} sx={{ p: '1rem 1rem 0.5rem' }}>
              <ControlledField
                control={control}
                name='common.executiveRatePctSmall'
                render={(renderProps) => (
                  <SliderInputField
                    {...renderProps}
                    label={t('PricingView.cost.vehicleClass.seatsRange', { seats: '6-30' })}
                    {...(!isStandardAvailable && {
                      tooltip: t('PricingView.cost.vehicleClass.missingVehicleClass', {
                        class: VehicleClass.STANDARD,
                      }),
                    })}
                    fieldProps={{
                      disabled: !isStandardAvailable,
                      valueLabelDisplay: 'auto',
                      valueLabelFormat: (value) => ` ${value}%`,
                      step: 5,
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ width: '100%' }}>
            <Box component={Paper} elevation={3} sx={{ p: '1rem 1rem 0.5rem' }} ref={sizeRef}>
              <ControlledField
                control={control}
                name='common.executiveRatePctLarge'
                render={(renderProps) => (
                  <SliderInputField
                    {...renderProps}
                    label={t('PricingView.cost.vehicleClass.seatsRange', { seats: '31-95' })}
                    {...(!isExecutiveAvailable && {
                      tooltip: t('PricingView.cost.vehicleClass.missingVehicleClass', {
                        class: VehicleClass.EXECUTIVE,
                      }),
                    })}
                    fieldProps={{
                      disabled: !isExecutiveAvailable,
                      valueLabelDisplay: 'auto',
                      valueLabelFormat: (value) => ` ${value}%`,
                      step: 5,
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid item gap={1}>
        <Typography variant='subtitle2' mb={1}>
          {t(`PricingView.cost.vehicleClass.rates.${VehicleClass.BASIC}`)}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} sx={{ width: '100%' }}>
            <Box component={Paper} elevation={3} sx={{ p: '1rem 1rem 0.5rem' }}>
              <ControlledField
                control={control}
                name='common.basicRatePctSmall'
                render={(renderProps) => {
                  return (
                    <SliderInputField
                      {...renderProps}
                      label={t('PricingView.cost.vehicleClass.seatsRange', { seats: '6-30' })}
                      {...(!isStandardAvailable && {
                        tooltip: t('PricingView.cost.vehicleClass.missingVehicleClass', {
                          class: VehicleClass.STANDARD,
                        }),
                      })}
                      fieldProps={{
                        min: -100,
                        max: 0,
                        disabled: !isStandardAvailable,
                        valueLabelDisplay: 'auto',
                        valueLabelFormat: (value) => ` ${value}%`,
                        step: 5,
                      }}
                    />
                  );
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ width: '100%' }}>
            <Box component={Paper} elevation={3} sx={{ p: '1rem 1rem 0.5rem' }}>
              <ControlledField
                control={control}
                name='common.basicRatePctLarge'
                render={(renderProps) => (
                  <SliderInputField
                    {...renderProps}
                    label={t('PricingView.cost.vehicleClass.seatsRange', { seats: '31-95' })}
                    {...(!isBasicAvailable && {
                      tooltip: t('PricingView.cost.vehicleClass.missingVehicleClass', {
                        class: VehicleClass.BASIC,
                      }),
                    })}
                    fieldProps={{
                      min: -100,
                      max: 0,
                      disabled: !isBasicAvailable,
                      valueLabelDisplay: 'auto',
                      valueLabelFormat: (value) => ` ${value}%`,
                      step: 5,
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

const columns = [
  {
    field: 'seatsFrom',
    sortable: false,
  },
  {
    field: 'fuelLitersPerDistanceUnit',
    sortable: false,
  },
  {
    field: 'driverHourlyRate',
    sortable: false,
  },
  {
    field: 'vehicleCostsPerDistanceUnit',
    sortable: false,
  },
  {
    field: 'vehicleCostsPerHour',
    sortable: false,
  },
  {
    field: 'adminCostsPerHour',
    sortable: false,
  },
  {
    field: 'departureCost',
    sortable: false,
  },
  {
    field: 'dailyMargin',
    sortable: false,
  },
];

function CarrierPricingCostsForm({
  defaultValues,
}: {
  defaultValues?: CarrierPricingCostsSchemaType;
}) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const currency = useOperatorCurrency();
  const distanceUnit = useOperatorDistanceUnit();
  const formInstance = useForm<CarrierPricingCostsSchemaType>({
    defaultValues: {
      bySeats: [],
      common: {
        dailyAllowance: undefined,
        basicRatePctLarge: undefined,
        basicRatePctSmall: undefined,
        executiveRatePctLarge: undefined,
        executiveRatePctSmall: undefined,
        fuelPriceLiterExclVat: undefined,
      },
      ...defaultValues,
    },
    resolver: zodResolver(carrierPricingCostsSchema),
  });
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;
  const pricingRulesMutation = useOperatorsPricingRulesMutation();

  const { fields } = useFieldArray({
    name: 'bySeats',
    control,
  });

  const onValid = async (data: CarrierPricingCostsSchemaType) =>
    pricingRulesMutation.mutateAsync(data);

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Grid container gap={2}>
        <Grid item xs={12} textAlign='right' display='flex' justifyContent='flex-end'>
          <Button
            type='submit'
            disabled={isSubmitting || isValidating || isLoading}
            variant='contained'
            fullWidth
            sx={{ maxWidth: 'max-content' }}
          >
            {t('save')}
          </Button>
        </Grid>
        <Grid container gap={4}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} sx={{ maxWidth: { sm: '200px!important' } }}>
              <ControlledField
                control={control}
                name='common.dailyAllowance'
                fieldProps={{
                  InputProps: {
                    endAdornment: <EndAdornment text={currency} />,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ maxWidth: { sm: '200px!important' } }}>
              <ControlledField
                control={control}
                name='common.fuelPriceLiterExclVat'
                fieldProps={{
                  InputProps: {
                    endAdornment: <EndAdornment text={currency} />,
                  },
                }}
              />
            </Grid>
          </Grid>
          <CarrierVehicleClassPricingFields form={formInstance} />
        </Grid>
        <Grid item xs={12} textAlign='left' mt={3}>
          <Paper sx={{ width: '100%', background: 'transparent', overflowX: 'auto' }}>
            <Paper elevation={3}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      '& > *:first-of-type': { paddingLeft: '16px' },
                      '& > *:last-of-type': { paddingRight: '16px' },
                    }}
                  >
                    {columns.map(({ field }) => (
                      <TableCell
                        key={field}
                        sx={{
                          padding: '10px 6px',
                          borderBottom: 'none',
                          color: colors.uiBlue600,
                          fontSize: '14px',
                          fontWeight: 400,
                          lineHeight: '20px',
                        }}
                      >
                        <TooltipTableHeader
                          tooltip={t(`PricingView.cost.table.header.tooltip.${field}`, {
                            defaultValue: '',
                            currency,
                            distanceUnit: distanceUnit.toLowerCase(),
                          })}
                        >
                          {t(`PricingView.cost.table.header.${field}`, {
                            currency,
                            distanceUnit: distanceUnit.toLowerCase(),
                          })}
                        </TooltipTableHeader>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody
                  sx={{
                    '& > tr > td': { padding: '10px 6px' },
                    '& > tr:nth-of-type(odd)': { backgroundColor: colors.uiBlue50 },
                  }}
                >
                  {fields.map((field, number) => {
                    return (
                      <TableRow key={field.id}>
                        <TableCell sx={{ fontWeight: 500, textAlign: 'center' }}>
                          {field.seatsFrom}-{field.seatsTo}
                        </TableCell>
                        <TableCell>
                          <ControlledField
                            control={control}
                            name={`bySeats.${number}.fuelLitersPerDistanceUnit`}
                          />
                        </TableCell>
                        <TableCell>
                          <ControlledField
                            control={control}
                            name={`bySeats.${number}.driverHourlyRate`}
                            fieldProps={{
                              InputProps: { endAdornment: <EndAdornment text={currency} /> },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <ControlledField
                            control={control}
                            name={`bySeats.${number}.vehicleCostsPerDistanceUnit`}
                            fieldProps={{
                              InputProps: { endAdornment: <EndAdornment text={currency} /> },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <ControlledField
                            control={control}
                            name={`bySeats.${number}.vehicleCostsPerHour`}
                            fieldProps={{
                              InputProps: { endAdornment: <EndAdornment text={currency} /> },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <ControlledField
                            control={control}
                            name={`bySeats.${number}.adminCostsPerHour`}
                            fieldProps={{
                              InputProps: { endAdornment: <EndAdornment text={currency} /> },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <ControlledField
                            control={control}
                            name={`bySeats.${number}.departureCost`}
                            fieldProps={{
                              InputProps: { endAdornment: <EndAdornment text={currency} /> },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <ControlledField
                            control={control}
                            name={`bySeats.${number}.dailyMargin`}
                            fieldProps={{
                              InputProps: { endAdornment: <EndAdornment text={currency} /> },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Paper>
        </Grid>
      </Grid>
    </form>
  );
}

export default CarrierPricingCostsForm;
